import type { ValidatorFn } from '@angular/forms';
import type { 
  IFilterField,
  IFilterMatchValueType,
  IFilterOption,ValidateFilterValuesResponseDto, } from '@cdw-ae/common-data';
import type { Observable } from 'rxjs';

export function filterBarInterfaces(): string {
  return 'filter-bar-interfaces';
}

export type SelectionOption = IFilterField;

export interface CustomValidator {
  validator: ValidatorFn;
  errorKey: string;
  errorMessage: string;
}

export interface FieldMatcher extends IFilterField {
  suggestMatcher?: boolean;
  validators?: CustomValidator[];
  options: {
    setValue?: (arg0?: string) => string;
    toolTipFormatter?: (arg0: string) => string;
    chipFormatter?: (arg0: string) => string;
    dialogWidth?: string;
    validateFn?: (values: string[]) => Observable<ValidateFilterValuesResponseDto>;
  };
  editorOptions?: SelectFromFilterOptions | OneOfFilterOptions;
  hasEditor?: boolean;
}

export interface FilterBarField extends IFilterField {
  fieldMatchers: FieldMatcher[];
  masked?: boolean;
  singleUse?: boolean;
  hidden?: boolean;
  /**
   * Position in the "+ Add a filter" dropdown. Lower numbers appear first.
   * Fields without a value fall to the bottom and sort alphabetically
   * among themselves.
   */
  sortPriority?: number;
}

export type HeadlessSelectedFilterField = IFilterOption;

export interface SelectedFilterField {
  field: FilterBarField;
  selectedMatcher: FieldMatcher;
  matchValue: IFilterMatchValueType;
  uid: string;
}

/**
 * Collapsed OU tree selection sent as the ouTreePicker matchValue.
 *
 * - exact: individually selected OU paths (not part of a fully-selected subtree)
 * - subtrees: parent OU paths where the parent and ALL descendants are selected
 * - totalCount: original number of individual OUs selected (display-only, ignored by the API)
 */
export interface CollapsedOuSelection {
  exact: string[];
  subtrees: string[];
  totalCount: number;
}

export interface SelectFromFilterOptions {
  selectionOptions: (SelectionOption & {
    defaultTrue?: boolean;
  })[];
}

export interface OneOfFilterOptions {
  selectionOptions: (SelectionOption & {
    defaultSelected?: boolean;
  })[];
}

export interface SuggestedMatcher {
  field: FilterBarField;
  suggestedMatcher: FieldMatcher;
}

// Added IGetDataParams, IGetSqlDataParams, SqlResponse
// from @cdw-ae/common-ui-components-search-grid:
export type IGetDataParams = {
  activeFilters: SelectedFilterField[];
  maxResults?: number;
};

export type IGetSqlDataParams = IGetDataParams & {
  supplementalFilters: HeadlessSelectedFilterField[];
  startRow?: number;
  endRow?: number;
  selectFields?: string[];
  sortOrder?: { name: string; direction?: 'ASC' | 'DESC' }[];
  /**
   * Restrict the result set to rows whose `id` is in this allow-list.
   * When present and non-empty, the data service should bypass the
   * normal filter chain and use the row-id-array wire format that the
   * backend's `mapAssetIdsToQueryDto` understands. Used by the SSRM
   * grid's "show changes only" UX so the user can see only their
   * pending edits across all pages.
   */
  idAllowList?: string[];
  /**
   * Opaque session identifier for the grid instance. Sourced from
   * `sessionStorage[grid-session:${viewKey}]` and minted as a UUIDv4 on
   * first grid mount per (viewKey) — see `CdwSsrmGridComponent.ngOnInit`.
   * The data service translates this into the `x-cdw-grid-session` HTTP
   * header on its outgoing call; the backend uses it (along with fbUserId,
   * customerId, viewKey) to key the user's server-side selection set in
   * Redis. Per-tab independent (sessionStorage scope), survives reloads
   * within the same tab.
   *
   * Optional — when omitted, the data service does not attach the header
   * and the backend falls back to its non-selection-aware code path
   * (full backwards compat).
   */
  gridSessionId?: string;
  /**
   * "Show selected only" toggle (EDUS-2485 Phase 7). When true, the data
   * service includes `useSelectionAsFilter=true` as a query string param;
   * the backend short-circuits to filtering by Redis selection-set
   * membership instead of by `activeFilters`, returning only the rows the
   * user has selected. No id array crosses the wire — kills the L1 latent
   * URL-length bug from PR #885 by construction.
   *
   * Optional — when omitted or false, the normal filter chain applies.
   */
  useSelectionAsFilter?: boolean;
};

export interface SqlResponse<T extends { id: string | number }> {
  data: T[];
  itemTotal: number;
}

export const isFilterBarField = (
  selectedField: unknown
): selectedField is FilterBarField =>
  !!selectedField &&
  typeof selectedField === 'object' &&
  'fieldMatchers' in selectedField;

export const isSuggestedMatcher = (
  selectedField: unknown
): selectedField is SuggestedMatcher =>
  !!selectedField &&
  typeof selectedField === 'object' &&
  'suggestedMatcher' in selectedField;

export const FilterBarDateTimeMatcherOptions = {
  lastHour: 'In the last hour',
  lastDay: 'In the last day',
  lastWeek: 'In the last week',
  beforeDateTime: 'Before',
  sinceDateTime: 'Since',
  betweenDateTime: 'Between',
} as const;
